<template>
  <div
    v-if="modelValue"
    class="dl-modal-overlay"
    data-testid="dl-send-modal"
    @click.self="cancel"
  >
    <div class="dl-modal" role="dialog" aria-modal="true">
      <header class="dl-modal__header">
        <h3 class="dl-modal__title" data-testid="dl-send-modal-title">
          Send for approval
        </h3>
      </header>

      <section class="dl-modal__body">
        <p class="dl-modal__decoration" data-testid="dl-send-modal-decoration">
          {{ decorationName }}
        </p>

        <fieldset class="dl-modal__fieldset">
          <legend>Clients</legend>
          <!--
            Issue #2179 Problem 3: a clean selectable list. Each row is a
            checkbox + the recipient name + the email as READ-ONLY
            secondary text (it looked like an editable field before,
            which confused operators). The email IS overridable for the
            legacy magic-link path, so an explicit "Change" affordance
            reveals the input on demand -- obvious editing, not a
            fake-editable box. The input keeps its ``dl-client-email-*``
            testid so the send payload contract is unchanged.
          -->
          <div
            v-for="c in clients"
            :key="c.id"
            class="dl-client-row"
            :data-testid="`dl-client-row-${c.id}`"
          >
            <label class="dl-client-row__select">
              <input
                v-model="selected[c.id]"
                type="checkbox"
                :data-testid="`dl-client-checkbox-${c.id}`"
              />
              <span class="dl-client-row__name">{{ c.name }}</span>
            </label>
            <div class="dl-client-row__email">
              <template v-if="!editingEmail[c.id]">
                <span
                  class="dl-client-row__email-text"
                  :data-testid="`dl-client-email-text-${c.id}`"
                >{{ recipientEmails[c.id] || "No email on file" }}</span>
                <button
                  type="button"
                  class="dl-btn dl-btn--link dl-client-row__email-change"
                  :data-testid="`dl-client-email-edit-${c.id}`"
                  @click="startEditingEmail(c.id)"
                >
                  Change
                </button>
              </template>
              <input
                v-else
                v-model.trim="recipientEmails[c.id]"
                type="email"
                class="dl-field__input"
                :data-testid="`dl-client-email-${c.id}`"
                :aria-label="`Recipient email for ${c.name}`"
              />
            </div>
          </div>
          <p
            v-if="!clients.length"
            class="dl-modal__empty"
            data-testid="dl-send-modal-no-clients"
          >
            No clients available. Add a client before sending for approval.
          </p>
        </fieldset>

        <label class="dl-field">
          <span class="dl-field__label">Message (optional)</span>
          <textarea
            v-model="message"
            class="dl-field__input"
            rows="3"
            data-testid="dl-send-modal-message"
            placeholder="Anything the client should know..."
          />
        </label>

        <p
          v-if="submitError"
          class="dl-modal__error"
          data-testid="dl-send-modal-error"
        >
          {{ submitError }}
        </p>
      </section>

      <footer class="dl-modal__footer">
        <button
          type="button"
          class="dl-btn dl-btn--link"
          data-testid="dl-send-modal-cancel"
          :disabled="submitting"
          @click="cancel"
        >
          Cancel
        </button>
        <button
          type="button"
          class="dl-btn dl-btn--primary"
          data-testid="dl-send-modal-submit"
          :disabled="!canSubmit"
          @click="submit"
        >
          {{ submitting ? "Sending..." : "Send for approval" }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
/**
 * SendForApprovalModal.vue - per-client send-for-approval modal.
 *
 * Cards:
 *   - "Decoration Library (Workstream B)" -- https://trello.com/c/u6NtgHwq
 *     (original Phase 1-late modal for the library list)
 *   - "Send-for-approval: route through ApprovalService (Option A)" --
 *     https://trello.com/c/7zSE3lyd (route the product-editor path
 *     through the Artwork pipeline)
 *
 * Two endpoint paths share this modal:
 *   - artworkId prop set (Option A path) -> POST
 *     ``/api/artworks/<artworkId>/send-for-approval`` with body
 *     ``{client_ids, message?}``. Used by the product editor's
 *     Send-for-approval button; the click resolves
 *     ``Variant.artwork_file_id`` to an ``Artwork.id`` (int shape) and
 *     passes it here. The Artwork pipeline's ApprovalService.send
 *     creates the ApprovalRecord and transitions
 *     ``Artwork.pipeline_state`` to ``awaiting_approval``.
 *   - artifactId prop set (legacy path) -> POST
 *     ``/api/decoration-artifacts/<artifactId>/send-for-approval`` with
 *     body ``{clients: [{client_id, recipient_email}], message?}``.
 *     Used by the Decoration Library list page; mints one magic-link
 *     token per client and sends the approval-request email.
 *
 * Exactly one of ``artworkId`` / ``artifactId`` should be set. Both
 * unset disables submit. If both are set, ``artworkId`` wins -- the
 * Option A path is the architecturally consistent one for new code.
 *
 * Multi-client selection per ADR 0017 (multi-client reuse):
 *   - Each selected client gets a fresh proof per client (no
 *     cross-reference).
 *   - The "Recipient email" field surfaces the destination address for
 *     the legacy magic-link path. The Option A path does not send an
 *     email today (the Artwork pipeline ApprovalRecord uses the
 *     client's record directly); the field is shown informationally so
 *     Owners can see who the approval is bound to before submitting.
 */
import axios from "@/axios";

export default {
  name: "SendForApprovalModal",
  props: {
    modelValue: { type: Boolean, default: false },
    // Option A path -- Artwork pipeline (product editor).
    artworkId: { type: [Number, String], default: null },
    // Legacy path -- DecorationArtifact pipeline (library list).
    artifactId: { type: [Number, String], default: null },
    decorationName: { type: String, default: "" },
    decorationVersion: { type: Number, default: 1 },
    clients: { type: Array, default: () => [] },
  },
  emits: ["update:modelValue", "sent"],
  data() {
    return {
      selected: {},
      recipientEmails: {},
      // Issue #2179 Problem 3: per-recipient "reveal the email input"
      // flag. Emails render as read-only text until the operator clicks
      // "Change"; then the ``dl-client-email-*`` input shows for that
      // client only.
      editingEmail: {},
      message: "",
      submitting: false,
      submitError: null,
    };
  },
  computed: {
    // True when this modal instance is configured for the Option A
    // (Artwork pipeline) path. Drives URL + body shape on submit.
    isArtworkPath() {
      return this.artworkId != null;
    },
    selectedClientIds() {
      const out = [];
      for (const c of this.clients) {
        if (this.selected[c.id]) {
          out.push(Number(c.id));
        }
      }
      return out;
    },
    selectedRecipientEmailsMap() {
      // Artwork path: a {clientId: email} map of selected clients that
      // have a (typed or prefilled) recipient email. JSON object keys
      // serialize as strings; the backend reads them via
      // ``recipient_emails.get(str(cid))``, so we key by String(c.id)
      // rather than Number() (which JSON coerces to a string anyway and
      // misleadingly implies numeric-key preservation). Clients with no
      // email are omitted so the backend falls back to the Client's
      // contact email.
      const out = {};
      for (const c of this.clients) {
        if (!this.selected[c.id]) continue;
        const email = this.recipientEmails[c.id];
        if (email && String(email).trim()) {
          out[String(c.id)] = String(email).trim();
        }
      }
      return out;
    },
    selectedClientsPayload() {
      // Legacy path: each selected client carries a recipient_email.
      const out = [];
      for (const c of this.clients) {
        if (this.selected[c.id] && this.recipientEmails[c.id]) {
          out.push({
            client_id: Number(c.id),
            recipient_email: String(this.recipientEmails[c.id]).trim(),
          });
        }
      }
      return out;
    },
    canSubmit() {
      if (this.submitting) return false;
      if (this.isArtworkPath) {
        // Artwork path: only need a target + at least one selected
        // client. Recipient email is informational, not required.
        return this.selectedClientIds.length > 0;
      }
      // Legacy path: artifactId + at least one selected client with a
      // recipient_email.
      return (
        this.artifactId != null
        && this.selectedClientsPayload.length > 0
      );
    },
  },
  mounted() {
    // Pre-fill on initial render too: the watcher below only runs on
    // CHANGE, so mounting with modelValue=true would otherwise leave
    // recipientEmails empty and submit would stay disabled.
    if (this.modelValue) {
      this.prefillRecipients();
    }
  },
  watch: {
    modelValue(open) {
      if (open) {
        this.resetForm();
        this.prefillRecipients();
      }
    },
  },
  methods: {
    resetForm() {
      this.selected = {};
      this.recipientEmails = {};
      this.editingEmail = {};
      this.message = "";
      this.submitting = false;
      this.submitError = null;
    },
    startEditingEmail(clientId) {
      // Reveal the email input for one client (Problem 3). Vue 3 reactive
      // object -- direct assignment is reactive.
      this.editingEmail[clientId] = true;
    },
    prefillRecipients() {
      // Pre-fill recipient with the client's primary contact email when
      // available on the client row. Saves a click per client.
      for (const c of this.clients) {
        if (c.primary_contact_email) {
          this.recipientEmails[c.id] = c.primary_contact_email;
        }
      }
    },
    cancel() {
      this.$emit("update:modelValue", false);
    },
    async submit() {
      this.submitting = true;
      this.submitError = null;
      let url;
      let payload;
      if (this.isArtworkPath) {
        // Option A path -- Artwork pipeline. Body shape:
        // {client_ids, recipient_emails?, message?}. The endpoint
        // resolves the artwork's current_version_id internally, calls
        // ApprovalService.send per client, mints a magic-link token,
        // and emails the client (demo scenario #8 -- artwork loop).
        //
        // ``recipient_emails`` is a {clientId: email} map so the
        // operator's typed recipient (the source of truth for demo
        // delivery) reaches the backend. Clients with no typed /
        // prefilled email are omitted; the backend then falls back to
        // the Client's contact email.
        url = `/api/artworks/${this.artworkId}/send-for-approval`;
        payload = { client_ids: this.selectedClientIds };
        const recipientEmails = this.selectedRecipientEmailsMap;
        if (Object.keys(recipientEmails).length) {
          payload.recipient_emails = recipientEmails;
        }
        if (this.message) payload.message = this.message;
      } else {
        // Legacy path -- DecorationArtifact pipeline. Body shape:
        // {clients: [{client_id, recipient_email}], message?}.
        url = (
          `/api/decoration-artifacts/${this.artifactId}/send-for-approval`
        );
        payload = { clients: this.selectedClientsPayload };
        if (this.message) payload.message = this.message;
      }
      try {
        const resp = await axios.post(url, payload);
        this.$emit("sent", resp.data);
        this.$emit("update:modelValue", false);
      } catch (err) {
        this.submitError =
          (err && err.response && err.response.data && err.response.data.error) ||
          (err && err.message) ||
          "Could not send for approval.";
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.dl-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.dl-modal {
  background: var(--tf-bg);
  border-radius: 8px;
  max-width: 560px;
  width: 90%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}
.dl-modal__header {
  padding: 16px 24px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}
.dl-modal__title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}
.dl-modal__body {
  padding: 16px 24px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.dl-modal__decoration {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.7);
  margin: 0;
}
.dl-modal__fieldset {
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  padding: 12px 16px;
}
.dl-modal__fieldset legend {
  font-size: 13px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.7);
  padding: 0 4px;
}
.dl-client-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 8px 0;
}
.dl-client-row__select {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 160px;
}
.dl-client-row__name {
  font-size: 14px;
}
.dl-client-row__email {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.dl-client-row__email-text {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.6);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.dl-client-row__email-change {
  padding: 2px 6px;
  font-size: 12px;
  flex: 0 0 auto;
}
.dl-modal__empty {
  color: rgba(0, 0, 0, 0.6);
  font-size: 14px;
  font-style: italic;
}
.dl-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.dl-field__label {
  font-size: 13px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.7);
}
.dl-field__input {
  padding: 8px 12px;
  border: 1px solid rgba(0, 0, 0, 0.23);
  border-radius: 4px;
  font-size: 14px;
}
.dl-modal__error {
  color: #d32f2f;
  font-size: 14px;
}
.dl-modal__footer {
  padding: 12px 24px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}
.dl-btn {
  padding: 8px 16px;
  border-radius: 4px;
  border: 1px solid transparent;
  font-size: 14px;
  cursor: pointer;
}
.dl-btn--primary {
  background: #1976d2;
  color: var(--tf-bg);
}
.dl-btn--primary:disabled {
  background: rgba(25, 118, 210, 0.4);
  cursor: not-allowed;
}
.dl-btn--link {
  background: none;
  border: none;
  color: rgba(0, 0, 0, 0.6);
}
</style>
